import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Html5Qrcode } from "html5-qrcode";
import { ArrowLeft, Camera, CheckCircle2, Loader2, MailCheck, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fmtDate } from "@/lib/api";
import { ErrorBox, Field, Logo } from "@/components/common";

export function Scanner({ onResult }) {
  const [err, setErr] = useState("");
  useEffect(() => {
    const s = new Html5Qrcode("qr-reader");
    let done = false;
    let started = false;
    s.start({ facingMode: "environment" }, { fps: 10, qrbox: 220 }, (text) => {
      if (done) return;
      done = true;
      s.stop().catch(() => {});
      onResult(text);
    }, () => {}).then(() => { started = true; }).catch(() => setErr("Camera unavailable. Allow camera access or enter your serial manually."));
    return () => { if (started && !done) s.stop().catch(() => {}); };
  }, [onResult]);
  return (
    <div className="space-y-2">
      <div id="qr-reader" className="overflow-hidden rounded-2xl bg-slate-900" data-testid="qr-scanner" />
      <ErrorBox error={err} />
    </div>
  );
}

const STEPS = ["Scan", "Card", "Details", "Verify"];

function Steps({ step }) {
  return (
    <div className="mb-6 grid grid-cols-4 gap-2">
      {STEPS.map((s, i) => (
        <div key={s} className="space-y-1">
          <div className={`h-1.5 rounded-full ${i <= step ? "gradient-brand" : "bg-slate-200"}`} />
          <p className={`text-[10px] font-bold uppercase ${i <= step ? "text-purple-700" : "text-slate-400"}`}>{s}</p>
        </div>
      ))}
    </div>
  );
}

function FamilyEditor({ family, setFamily }) {
  const [f, setF] = useState({ full_name: "", relationship: "CHILD", age: "" });
  return (
    <div className="space-y-2" data-testid="register-family">
      <p className="label">Family members (parents, spouse, children)</p>
      {family.map((m, i) => (
        <div key={i} className="flex items-center justify-between rounded-xl bg-slate-50 px-3 py-2 text-sm" data-testid={`register-family-${i}`}>
          <span><b>{m.full_name}</b> <span className="text-xs capitalize text-slate-500">· {m.relationship.toLowerCase()} · {m.age} yrs</span></span>
          <button type="button" onClick={() => setFamily(family.filter((_, j) => j !== i))}><Trash2 size={14} /></button>
        </div>
      ))}
      <div className="grid grid-cols-[1fr_1fr_auto] gap-2 sm:grid-cols-[1fr_110px_70px_auto]">
        <input className="field col-span-3 sm:col-span-1" placeholder="Full name" value={f.full_name} onChange={(e) => setF({ ...f, full_name: e.target.value })} data-testid="family-name-input" />
        <select className="field" value={f.relationship} onChange={(e) => setF({ ...f, relationship: e.target.value })} data-testid="family-relationship-select">
          <option value="CHILD">Child</option><option value="SPOUSE">Spouse</option><option value="PARENT">Parent</option>
        </select>
        <input className="field" type="number" min="0" placeholder="Age" value={f.age} onChange={(e) => setF({ ...f, age: e.target.value })} data-testid="family-age-input" />
        <button type="button" className="btn-secondary" disabled={!f.full_name || f.age === ""} data-testid="family-add-button"
          onClick={() => { setFamily([...family, { ...f, age: Number(f.age) }]); setF({ full_name: "", relationship: "CHILD", age: "" }); }}><Plus size={14} /></button>
      </div>
    </div>
  );
}

export default function Register() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { setUser } = useAuth();
  const [slug, setSlug] = useState(params.get("package"));
  const [step, setStep] = useState(params.get("package") ? 1 : 0);
  const [serial, setSerial] = useState("");
  const [card, setCard] = useState(null);
  const [form, setForm] = useState({ first_name: "", last_name: "", birthdate: "", occupation: "", address: "", email: "" });
  const [family, setFamily] = useState([]);
  const [terms, setTerms] = useState(false);
  const [privacy, setPrivacy] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [pw, setPw] = useState({ p: "", c: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const run = async (fn) => {
    setBusy(true);
    setError("");
    try { await fn(); } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  };
  const onScan = (text) => {
    const m = String(text).match(/\/enroll\/([a-z0-9-]+)/i);
    if (!m) return setError("That QR code is not a GIA CARE+ package code.");
    setSlug(m[1]);
    setStep(1);
  };
  const check = () => run(async () => { setCard((await api.post("/register/check", { serial, package_slug: slug || null })).data); setStep(2); });
  const sendOtp = () => run(async () => { await api.post("/register/send-otp", { serial: card.serial, email: form.email }); setOtpSent(true); toast.success("Verification code sent"); });
  const complete = () => run(async () => {
    if (pw.p !== pw.c) throw new Error("Passwords do not match");
    const { data } = await api.post("/register/complete", { ...form, serial: card.serial, otp, password: pw.p, family, accept_terms: terms, accept_privacy: privacy });
    setUser(data);
    toast.success("Registration complete. Welcome to GIA CARE+!");
    nav("/app", { replace: true });
  });
  const detailsOk = form.first_name && form.last_name && form.birthdate && form.occupation && form.address && form.email;

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50/60 via-white to-white">
      <div className="mx-auto max-w-md px-5 py-8">
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3"><Logo size={44} /><span className="text-lg font-extrabold text-gradient">GIA CARE+</span></div>
          <Link to="/login" className="btn-ghost btn-sm" data-testid="register-back-login"><ArrowLeft size={14} /> Sign in</Link>
        </div>
        <h1 className="mb-1 text-2xl font-extrabold tracking-tight">Register your card</h1>
        <p className="mb-6 text-sm text-slate-500">Only cards activated by an authorized GIA cashier can be registered.</p>
        <Steps step={step} />
        {step === 0 && (
          <div className="space-y-4" data-testid="register-step-scan">
            <p className="flex items-center gap-2 text-sm font-semibold"><Camera size={16} /> Scan the package QR code on your card</p>
            <Scanner onResult={onScan} />
            <button className="btn-ghost w-full" onClick={() => setStep(1)} data-testid="register-skip-scan">Enter card serial instead</button>
          </div>
        )}
        {step === 1 && (
          <div className="surface space-y-4 p-5" data-testid="register-step-serial">
            <Field label="Card serial number (printed on your card)">
              <input className="field font-mono uppercase" value={serial} onChange={(e) => setSerial(e.target.value)} placeholder="GIA-2026-000001" data-testid="register-serial-input" />
            </Field>
            <button className="btn-primary w-full" onClick={check} disabled={busy || serial.length < 4} data-testid="register-check-button">{busy && <Loader2 size={15} className="animate-spin" />} Validate card</button>
          </div>
        )}
        {step === 2 && card && (
          <div className="space-y-4" data-testid="register-step-details">
            <div className="flex gap-3 rounded-2xl bg-emerald-50 p-4 text-xs text-emerald-900" data-testid="register-card-valid">
              <CheckCircle2 size={18} className="shrink-0" />
              <div><b>{card.package_name}</b> · <span className="font-mono">{card.serial}</span><br />Activated {fmtDate(card.activated_at)} · valid until {fmtDate(card.expires_at)}</div>
            </div>
            <div className="surface space-y-3 p-5">
              <div className="grid grid-cols-2 gap-3">
                <Field label="First name"><input className="field" value={form.first_name} onChange={set("first_name")} data-testid="register-first-name" /></Field>
                <Field label="Last name"><input className="field" value={form.last_name} onChange={set("last_name")} data-testid="register-last-name" /></Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Date of birth"><input className="field" type="date" value={form.birthdate} onChange={set("birthdate")} data-testid="register-birthdate" /></Field>
                <Field label="Occupation"><input className="field" value={form.occupation} onChange={set("occupation")} data-testid="register-occupation" /></Field>
              </div>
              <Field label="Home address"><input className="field" value={form.address} onChange={set("address")} data-testid="register-address" /></Field>
              <Field label="Email address" hint={`Use the email given to the cashier at purchase (${card.email_hint})`}>
                <input className="field" type="email" value={form.email} onChange={set("email")} data-testid="register-email" />
              </Field>
              <FamilyEditor family={family} setFamily={setFamily} />
            </div>
            <button className="btn-primary w-full" disabled={!detailsOk} onClick={() => setStep(3)} data-testid="register-details-next">Continue</button>
          </div>
        )}
        {step === 3 && (
          <div className="surface space-y-4 p-5" data-testid="register-step-verify">
            <label className="flex gap-2 text-sm"><input type="checkbox" checked={terms} onChange={(e) => setTerms(e.target.checked)} data-testid="register-terms-check" /> I accept the GIA CARE+ Terms and Conditions.</label>
            <label className="flex gap-2 text-sm"><input type="checkbox" checked={privacy} onChange={(e) => setPrivacy(e.target.checked)} data-testid="register-privacy-check" /> I acknowledge the Privacy Notice and consent to processing of my health information (Data Privacy Act of 2012).</label>
            <button className="btn-secondary w-full" disabled={!terms || !privacy || busy} onClick={sendOtp} data-testid="register-send-otp"><MailCheck size={15} /> {otpSent ? "Resend code" : "Send verification code"} to {form.email}</button>
            {otpSent && (
              <>
                <Field label="6-digit code"><input className="field text-center font-mono text-lg tracking-[0.4em]" inputMode="numeric" maxLength={6} value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))} data-testid="register-otp-input" /></Field>
                <Field label="Create password" hint="At least 8 characters with letters and numbers"><input className="field" type="password" value={pw.p} onChange={(e) => setPw({ ...pw, p: e.target.value })} data-testid="register-password" /></Field>
                <Field label="Confirm password"><input className="field" type="password" value={pw.c} onChange={(e) => setPw({ ...pw, c: e.target.value })} data-testid="register-password-confirm" /></Field>
                <button className="btn-primary w-full" disabled={busy || otp.length !== 6 || !pw.p} onClick={complete} data-testid="register-complete-button">{busy && <Loader2 size={15} className="animate-spin" />} Complete registration</button>
              </>
            )}
            <button className="btn-ghost btn-sm" onClick={() => setStep(2)} data-testid="register-verify-back">Back</button>
          </div>
        )}
        <div className="mt-4"><ErrorBox error={error} /></div>
      </div>
    </div>
  );
}
